import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { US_STATES_OPTIONS, ADDRESS_LINE_VALIDATION, CITY_VALIDATION } from '@/utils/constant';
import { getUsersByTeam, serverSearch, teamSearch } from '@/services/Network';
import type { UserGetAllParams } from '@/types/user.types';
import type { HIEditFormValues } from './hi.schema';
import { useUserStore } from '@/stores/user';
import { isAdminOrSupervisor } from '@/services/userService';
import { hasFeature } from '@/services/featureService';
import { Feature } from '@/utils/constant';

const SearchIcon = {
  Icon: Search,
  iconProps: { className: 'text-[#969696] h-5 w-5' },
};

export const HIEditServiceDetails = () => {
  const { control, setValue, watch } = useFormContext<HIEditFormValues>();
  const rushType = watch('rushType');
  const teamId = watch('teamId');
  const loggedInUser = useUserStore((state) => state.user);
  const isSupervisorOrAdmin = isAdminOrSupervisor(loggedInUser);
  const canAssign = hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_CREATE);

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="mortgageCompanyOrPlaintiff"
          label="Mortgage Co / Plaintiff"
          placeholder="Mortgage Co / Plaintiff"
          required
        />
        <FormFieldComponent
          control={control}
          name="ownerOrDefendantName"
          label="Owner's Name / Defendant"
          placeholder="Owner's Name / Defendant"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="addressLine1"
          label="Address Line 1"
          placeholder="Address Line 1"
          required
          {...ADDRESS_LINE_VALIDATION}
        />
        <FormFieldComponent
          control={control}
          name="addressLine2"
          label="Address Line 2"
          placeholder="Address Line 2"
          {...ADDRESS_LINE_VALIDATION}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="city"
          label="City"
          placeholder="City"
          required
          {...CITY_VALIDATION}
        />
        <FormFieldSelect
          name="state"
          options={US_STATES_OPTIONS}
          placeholder="Select State"
          label="State"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="zipCode"
          label="Zip Code"
          placeholder="Zip Code"
          required
        />
        <FormFieldCalendar
          control={control}
          name="lastServeDate"
          label="Last Serve Date"
          placeholder="Last Serve Date"
          disabled={rushType !== 'NONE'}
          clearable={rushType === 'NONE'}
        />
      </div>

      {canAssign && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
          <FormFieldSearch
            name="teamId"
            label="Assign Team"
            placeholder="Search Team"
            props={SearchIcon}
            api={teamSearch}
            onBlur={(value) => {
              if (!value) {
                setValue('teamId', null, { shouldDirty: true });
                setValue('userId', null, { shouldDirty: true });
              }
            }}
            onChange={() => {
              setValue('userId', null, { shouldDirty: true });
            }}
          />
          {teamId && (
            <FormFieldSearch
              name="userId"
              label="Assign User"
              placeholder="Search User"
              props={SearchIcon}
              searchFieldName="search"
              disabled={!isSupervisorOrAdmin}
              api={(params) =>
                getUsersByTeam(
                  {
                    teamId,
                    userName: params.search ?? undefined,
                    userStatus: 'ACTIVE',
                  },
                  isSupervisorOrAdmin
                )
              }
              onBlur={(value) => {
                if (!value) setValue('userId', null, { shouldDirty: true });
              }}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<UserGetAllParams>
          name="serverId"
          label="Server Name"
          placeholder="Search Server"
          props={SearchIcon}
          api={(params) => serverSearch({ ...params })}
        />
      </div>
    </div>
  );
};
